<script lang="ts">
export default {
	name: "GalleryPageVue"
};
</script>

<script setup lang="ts">
import * as vue from "vue";
import { useGalleries } from "@/composables/useGalleries";
import { GalleryContainerVue } from "@components/index";
import { onMounted, onUnmounted } from "vue";

const { galleries } = useGalleries();
const collections = vue.computed(() => galleries.value);

const activeCollectionId = vue.ref<string>("");
const containerRefs = vue.ref<Record<string, any>>({});

const scrollToTop = () => {
	const scrollPanel = document.querySelector(".p-scrollpanel-content");
	if (scrollPanel) {
		scrollPanel.scrollTo({ top: 0, behavior: "smooth" });
	}
};

const setContainerRef = (id: string) => (el: any) => {
	if (el && el.containerEl) {
		containerRefs.value[id] = el.containerEl;
	}
};

onMounted(() => {
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					activeCollectionId.value = (entry.target as HTMLElement).dataset.id ?? "";
				}
			});
		},
		{ threshold: 0.3, rootMargin: "-10% 0px -10% 0px" }
	);

	vue.watch(
		containerRefs,
		(refs) => {
			Object.values(refs).forEach((el) => {
				if (el) observer.observe(el);
			});
		},
		{ immediate: true, deep: true }
	);

	onUnmounted(() => observer.disconnect());
});
</script>

<template>
	<div id="gallery" class="gallery-page" :class="{ 'gallery-page--dark': activeCollectionId === 'live-music' }">
		<GalleryContainerVue
			v-for="collection in collections"
			:ref="setContainerRef(collection.banner.id)"
			:data-id="collection.banner.id"
			:banner="collection.banner"
			:images="collection.images"
			:key="collection.banner.id"
		/>
		<div class="gallery-page__scroll-to-top" @click="scrollToTop">
			<i class="pi pi-arrow-up"></i>
		</div>
	</div>
</template>

<style scoped lang="scss">
.gallery-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	justify-content: center;
	margin-left: auto;
	margin-right: auto;
	margin-bottom: 1rem;
	min-height: 100vh;
	background-color: #fff;
	transition: background-color 0.3s ease;
}

.gallery-page--dark {
	background-color: #000;
}

.gallery-page__scroll-to-top {
	display: block;
	position: fixed;
	bottom: 2rem;
	right: 2rem;
	z-index: 10;
	height: 3rem;
	width: 3rem;
	min-width: 44px;
	min-height: 44px;
	font-size: 1.5rem;
	line-height: 3rem;
	text-align: center;
	background-color: #000;
	color: #fff;
	border-radius: 50%;
	box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.5);
	cursor: pointer;
	transition: all 0.2s ease-in-out;
}

.gallery-page__scroll-to-top:hover {
	background-color: #fff;
	color: #000;
	transform: scale(1.2);
}

.gallery-page--dark .gallery-page__scroll-to-top {
	background-color: #fff;
	color: #000;
}

.gallery-page--dark .gallery-page__scroll-to-top:hover {
	background-color: #000;
	color: #fff;
}
</style>
